import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { Chip, IconButton, Tooltip, Badge, MenuItem } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import Back from "../../../../../components/Back";
import type { ServiceProcedure as Service } from "../../../../../interfaces";
import { useServiceProcedure } from "../../../../../api/catalog_procedure/use-service-procedure";
import { GlobalModal } from "../../../../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import { downloadServiceProcedureFiles } from "../../../../../api/catalog_procedure/use-procedure-download";
import { useSnackbar } from "../../../../../context/SnackbarContext";
import FormattedDateCell from "../../../../../components/FormattedDateCell";

const ServiceProcedure = () => {
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModalData, setJsonModalData] = useState<any>(null);
  const [jsonModalTitle, setJsonModalTitle] = useState("");
  const { notify } = useSnackbar();

  const handleOpenJsonModal = (title: string, data: any) => {
    setJsonModalTitle(title);
    setJsonModalData(data);
    setJsonModalOpen(true);
  };

  const handleDownload = async (procedureId: number) => {
    try {
      const blob = await downloadServiceProcedureFiles(procedureId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `service_procedure_${procedureId}_files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      notify("Error downloading file", "error");
    }
  };

  const {
    data: serviceData = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useServiceProcedure();

  const columns = useMemo<MRT_ColumnDef<Service>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      { accessorKey: "user_id", header: "User ID" },
      { accessorKey: "catalog_id", header: "Catalog ID" },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = cell.getValue<string>().toUpperCase();
          let chipColor:
            | "default"
            | "primary"
            | "secondary"
            | "error"
            | "info"
            | "success"
            | "warning" = "default";

          switch (status) {
            case "INIT":
              chipColor = "warning";
              break;
            case "IN_PROGRESS":
              chipColor = "info";
              break;
            case "SUCCESS":
              chipColor = "success";
              break;
            case "ERROR":
              chipColor = "error";
              break;
            default:
              chipColor = "default";
          }

          return <Chip label={status} color={chipColor} sx={{ width: 150 }} />;
        },
      },
      { accessorKey: "agent_id", header: "Agent ID" },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: serviceData,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: { sx: { maxHeight: "calc(78vh - 112px)" } },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
      sorting: [{ id: "created_at", desc: true }],
    },
    enableColumnResizing: true,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid",
    muiToolbarAlertBannerProps: error
      ? { color: "error", children: "Error loading data" }
      : undefined,
    state: {
      isLoading,
      showAlertBanner: !!error,
      showProgressBars: isRefetching,
    },
    renderTopToolbarCustomActions: () => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => refetch()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem
        key={0}
        onClick={() => handleOpenJsonModal("Payload", row.original.payload)}
        sx={{ m: 0 }}
      >
        View Payload
      </MenuItem>,
      <MenuItem
        key={1}
        onClick={() => handleOpenJsonModal("Result", row.original.result)}
        sx={{ m: 0 }}
      >
        View Result
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => handleDownload(row.id)}
        disabled={!(row.original.files.length > 0)}
      >
        Download
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title>Service Procedure</Title>
      {error && <div>Error loading data</div>}
      <MaterialReactTable table={table} />
      <GlobalModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        title={jsonModalTitle}
      >
        <JsonEditor
          minWidth="100%"
          data={jsonModalData}
          restrictAdd
          restrictDelete
          restrictEdit
        />
      </GlobalModal>
      <Back />
    </>
  );
};

export default ServiceProcedure;
